import React, { useMemo } from 'react';
import {
  LPToken,
  RewardProgram,
  Subscription as SubscriptionType
} from 'types';
import { ModalType } from 'components/modals/types';
import { Crucible } from 'types';
import { BigNumber } from 'ethers';
import { useModal } from 'store/modals';
import { AiFillInfoCircle, FaLock } from 'react-icons/all';
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  SimpleGrid,
  Skeleton,
  Spinner,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  useMediaQuery
} from '@chakra-ui/react';
import { LightMode } from '@chakra-ui/color-mode';
import { simpleLPToken } from 'types';
import { useSubscriptions } from 'hooks/query/useSubscriptions';
import formatNumber from 'utils/formatNumber';
import GradientBackground from 'components/shared/GradientBackground';
import { PowerSwitchStatus } from 'api/blockchain/rewardProgram';

type Props = {
  crucible: Crucible;
  subscription: SubscriptionType;
  isReadonly?: boolean;
  rewardProgramState?: PowerSwitchStatus;
  currentRewardProgram: RewardProgram;
};

const BottomProgressBar: React.FC<{
  completionPercent: string;
  completionDays: number;
  rewardScalingTime: number;
}> = ({ completionPercent, completionDays, rewardScalingTime }) => {
  return (
    <Tooltip
      label={`Progress to max multiplier: ${completionDays} of ${rewardScalingTime} days (${completionPercent})`}
    >
      <Box position='relative'>
        <Box
          position='absolute'
          bg='gray.500'
          w='100%'
          h='5px'
          bottom='0px'
          borderRadius='0 0 8px 8px'
        />
        <Box
          position='absolute'
          bg='cyan.500'
          w={completionPercent}
          h='5px'
          bottom='0px'
          borderRadius={
            completionPercent === '100%' ? '0 0 8px 8px' : '0 0 0 8px'
          }
        />
      </Box>
    </Tooltip>
  );
};

const TokenRewardStatBox: React.FC<{
  tokenSymbol: string;
  tokenAmount: BigNumber;
  usdValue: number;
  tokenDecimals: number;
}> = ({ tokenSymbol, tokenAmount, usdValue, tokenDecimals }) => {
  return (
    <Box py={2} bg='gray.600' borderRadius='xl'>
      <Tooltip
        label={
          <>
            <Text>
              {formatNumber.tokenFull(tokenAmount, tokenDecimals)} {tokenSymbol}
            </Text>
            <Text>Value ${usdValue ? usdValue.toFixed(2) : '-'}</Text>
          </>
        }
        fontSize='md'
        placement='top'
      >
        <Text isTruncated fontSize='sm'>
          {tokenSymbol}: {formatNumber.token(tokenAmount, tokenDecimals)}
        </Text>
      </Tooltip>
    </Box>
  );
};

const Subscription: React.FC<Props> = ({
  crucible,
  subscription,
  isReadonly,
  rewardProgramState,
  currentRewardProgram
}) => {
  const { openModal } = useModal();
  const { data: subscriptionsData } = useSubscriptions();
  const { subscriptions, subscriptionsMeta } = subscriptionsData!;
  const { stakingToken } = subscriptionsData!.subscriptionsMeta || {};
  const { rewardScalingTimestamp, rewardScalingTime } = subscriptionsMeta;

  const [isLargerThan510] = useMediaQuery('(min-width: 511px)');

  const lpTokenData = (subscription.stakingToken as LPToken)?.lpTokenData;

  const isSubscriptionComplete =
    Math.min(subscription.timeElapsed / rewardScalingTimestamp, 1) === 1;

  /* get days remaining text */
  const secondsInDay = 60 * 60 * 24;
  const daysRemainingCopy = `${Math.min(
    Math.floor(subscription.timeElapsed / secondsInDay),
    rewardScalingTime
  )} of ${rewardScalingTime} days`;

  /* get total rewards value in USD */
  const totalRewardsUSD = useMemo(() => {
    const totalBonus = subscription.rewards.bonusTokens.reduce((acc, obj) => {
      return acc + (obj.value.amountUSD || 0);
    }, 0);
    return totalBonus + (subscription.rewards.rewardToken.value.amountUSD || 0);
  }, [subscription]);

  const renderLPValue = useMemo(() => {
    if (lpTokenData && Object.keys(lpTokenData).length > 0) {
      const lpTokenArray = lpTokenData.map((token: simpleLPToken) => {
        return (
          formatNumber.token(token.amount, token.decimals) + ' ' + token.symbol
        );
      });
      return (
        <>
          <Text pt='5px'>
            <strong>LP Value</strong>
          </Text>
          <Text>{lpTokenArray.join(' - ')}</Text>
        </>
      );
    }
    return null;
  }, [lpTokenData]);

  return (
    <GradientBackground
      mb={4}
      visible={isSubscriptionComplete}
      borderRadius='xl'
    >
      <AccordionItem
        bg='gray.700'
        borderRadius='xl'
        overflow='hidden'
        border='none'
      >
        <div>
          <h2>
            <AccordionButton
              pb={4}
              display='block'
              position='relative'
              bg='gray.700'
              borderRadius='8px 8px 0 0'
            >
              <AccordionIcon position='absolute' top='5px' right='5px' />
              <Grid templateColumns='repeat(24, 1fr)' gap={3} py={2}>
                <GridItem colSpan={[12, null, 9]} justifySelf='left'>
                  <Tooltip
                    label={
                      !subscription ? (
                        <Spinner size='sm' />
                      ) : (
                        <>
                          <Text>
                            {formatNumber.tokenFull(
                              subscription.stakingToken.value.amount,
                              subscription.stakingToken.decimals
                            ) +
                              ' ' +
                              stakingToken?.tokenSymbol}
                          </Text>
                          <Text>
                            USD Value $
                            {subscription.stakingToken.value.amountUSD?.toFixed(
                              2
                            )}
                          </Text>
                          {renderLPValue}
                        </>
                      )
                    }
                    placement='bottom'
                  >
                    <Tag
                      maxWidth='140px'
                      height='28px'
                      size='lg'
                      backgroundColor='gray.600'
                      borderRadius='full'
                      isTruncated
                    >
                      <TagLeftIcon boxSize='10px' as={FaLock} />
                      <TagLabel fontSize='xs'>
                        {formatNumber.token(
                          subscription.stakingToken.value.amount,
                          subscription.stakingToken.decimals
                        )}{' '}
                        <strong>{stakingToken?.tokenSymbol}</strong>
                      </TagLabel>
                    </Tag>
                  </Tooltip>
                </GridItem>

                <GridItem colSpan={[12, null, 5]}>
                  <Box fontSize='sm' isTruncated>
                    <Text color='gray.300' textAlign='left'>
                      Started
                    </Text>
                    <Text textAlign='left'>
                      {formatNumber.date(subscription.subscriptionDate * 1000)}
                    </Text>
                  </Box>
                </GridItem>

                <GridItem colSpan={[12, null, 5]}>
                  <Box fontSize='sm' isTruncated>
                    <Box color='gray.300' display='flex' alignItems='center'>
                      <span style={{ marginRight: 5 }}>Value</span>
                      <Tooltip label='This is the combined value of your subscribed token and your rewards'>
                        <span>
                          <AiFillInfoCircle />
                        </span>
                      </Tooltip>
                    </Box>
                    <LightMode>
                      <Skeleton
                        isLoaded={!!subscription}
                        startColor='gray.500'
                        endcolor='gray.600'
                      >
                        <Text textAlign='left'>
                          $
                          {(
                            totalRewardsUSD +
                            (subscription.stakingToken.value.amountUSD || 0)
                          ).toFixed(2)}
                        </Text>
                      </Skeleton>
                    </LightMode>
                  </Box>
                </GridItem>

                <GridItem colSpan={[12, null, 5]}>
                  <Box fontSize='sm' isTruncated>
                    <Box color='gray.300' display='flex' alignItems='center'>
                      <span style={{ marginRight: 5 }}>Rewards</span>
                      <Tooltip label='The rewards here are calculated by your current share of the reward pool at this exact moment. This value can fluctuate depending on the pool activity or token value'>
                        <span>
                          <AiFillInfoCircle />
                        </span>
                      </Tooltip>
                    </Box>
                    <LightMode>
                      <Skeleton
                        isLoaded={!!subscription}
                        startColor='gray.500'
                        endcolor='gray.600'
                      >
                        <Text textAlign='left'>
                          ${totalRewardsUSD.toFixed(2)}
                        </Text>
                      </Skeleton>
                    </LightMode>
                  </Box>
                </GridItem>
              </Grid>
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <SimpleGrid columns={[1, 2, 3]} gap={4}>
              {subscription?.rewards?.rewardToken && (
                <TokenRewardStatBox
                  tokenAmount={subscription.rewards.rewardToken.value.amount}
                  tokenSymbol={subscription.rewards.rewardToken.symbol}
                  usdValue={subscription.rewards.rewardToken.value.amountUSD!}
                  tokenDecimals={subscription.rewards.rewardToken.decimals!}
                />
              )}
              {subscription?.rewards?.bonusTokens.length > 0 &&
                subscription?.rewards?.bonusTokens.map((bonusToken) => (
                  <TokenRewardStatBox
                    key={bonusToken.address}
                    tokenAmount={bonusToken.value.amount}
                    tokenSymbol={bonusToken.symbol}
                    usdValue={bonusToken.value.amountUSD!}
                    tokenDecimals={bonusToken.decimals!}
                  />
                ))}
            </SimpleGrid>

            <Flex
              justifyContent={isReadonly ? 'flex-end' : 'space-between'}
              flexWrap='wrap'
              mt={6}
            >
              {!isLargerThan510 && (
                <Tooltip label='This is your current progress for the reward multiplier scaling period'>
                  <Text color='gray.500' lineHeight='40px'>
                    {daysRemainingCopy}
                  </Text>
                </Tooltip>
              )}
              {!isReadonly && (
                <Button
                  size='sm'
                  borderRadius='lg'
                  isFullWidth={!isLargerThan510}
                  onClick={() =>
                    rewardProgramState === PowerSwitchStatus.OFFLINE ||
                    rewardProgramState === PowerSwitchStatus.SHUTDOWN
                      ? openModal(ModalType.rageQuit, {
                          type: rewardProgramState,
                          crucibleId: crucible.id,
                          currentRewardProgram,
                          stakingTokenAddress: stakingToken.address
                        })
                      : openModal(ModalType.claimRewards, {
                          crucibleId: crucible.id,
                          subscriptionIdx: subscriptions.findIndex(
                            (s) =>
                              s.subscriptionDate ===
                              subscription.subscriptionDate
                          ),
                          stakingTokenSymbol: stakingToken?.tokenSymbol
                        })
                  }
                  disabled={!subscriptions}
                >
                  {rewardProgramState === PowerSwitchStatus.OFFLINE ||
                  rewardProgramState === PowerSwitchStatus.SHUTDOWN
                    ? 'Forced Exit'
                    : 'Claim & Unsubscribe'}
                </Button>
              )}
              {isLargerThan510 && (
                <Tooltip label='This is your current progress for the reward multiplier scaling period'>
                  <Text color='gray.300' lineHeight='40px'>
                    {daysRemainingCopy}
                  </Text>
                </Tooltip>
              )}
            </Flex>
          </AccordionPanel>
          <BottomProgressBar
            completionPercent={formatNumber.percent(
              Math.min(subscription.timeElapsed / rewardScalingTimestamp, 1)
            )}
            completionDays={Math.min(
              Math.floor(subscription.timeElapsed / (60 * 60 * 24)),
              rewardScalingTime
            )}
            rewardScalingTime={rewardScalingTime}
          />
        </div>
      </AccordionItem>
    </GradientBackground>
  );
};

export default Subscription;
